import React, { useEffect, useRef, useState } from "react";
import { CustomDateField } from "../../components/CustomDateField";

type OpenHourType = {
  id: number;
  locationId: number;
  startDate: string;
  endDate: string;
  isSundayOpen: boolean;
  sundayOpenTime: string;
  sundayCloseTime: string;
  isMondayOpen: boolean;
  mondayOpenTime: string;
  mondayCloseTime: string;
  isTuesdayOpen: boolean;
  tuesdayOpenTime: string;
  tuesdayCloseTime: string;
  isWednesdayOpen: boolean;
  wednesdayOpenTime: string;
  wednesdayCloseTime: string;
  isThursdayOpen: boolean;
  thursdayOpenTime: string;
  thursdayCloseTime: string;
  isFridayOpen: boolean;
  fridayOpenTime: string;
  fridayCloseTime: string;
  isSaturdayOpen: boolean;
  saturdayOpenTime: string;
  saturdayCloseTime: string;
  uniqueId: number;
};

type Props = {
  index: number;
  uniqueNumber: number;
  data: OpenHourType;
  changeDataOnOpenHour: (data: OpenHourType, index: number) => void;
  deleteTheFleetOpenHour: (index: number) => void;
  disabled?: boolean;
};

export default function AddOpenHour({
  index,
  uniqueNumber,
  data,
  changeDataOnOpenHour,
  deleteTheFleetOpenHour,
  disabled = false,
}: Props) {
  const isHydratingRef = useRef(true);
  const [startDate, setStartDate] = useState<string>(data.startDate);
  const [endDate, setEndDate] = useState<string>(data.endDate);
  const [isSundayOpen, setIsSundayOpen] = useState<boolean>(data.isSundayOpen);
  const [sundayOpenTime, setSundayOpenTime] = useState<string>(
    data.sundayOpenTime
  );
  const [sundayCloseTime, setSundayCloseTime] = useState<string>(
    data.sundayCloseTime
  );
  const [isMondayOpen, setIsMondayOpen] = useState<boolean>(data.isMondayOpen);
  const [mondayOpenTime, setMondayOpenTime] = useState<string>(
    data.mondayOpenTime
  );
  const [mondayCloseTime, setMondayCloseTime] = useState<string>(
    data.mondayCloseTime
  );
  const [isTuesdayOpen, setIsTuesdayOpen] = useState<boolean>(
    data.isTuesdayOpen
  );
  const [tuesdayOpenTime, setTuesdayOpenTime] = useState<string>(
    data.tuesdayOpenTime
  );
  const [tuesdayCloseTime, setTuesdayCloseTime] = useState<string>(
    data.tuesdayCloseTime
  );
  const [isWednesdayOpen, setIsWednesdayOpen] = useState<boolean>(
    data.isWednesdayOpen
  );
  const [wednesdayOpenTime, setWednesdayOpenTime] = useState<string>(
    data.wednesdayOpenTime
  );
  const [wednesdayCloseTime, setWednesdayCloseTime] = useState<string>(
    data.wednesdayCloseTime
  );
  const [isThursdayOpen, setIsThursdayOpen] = useState<boolean>(
    data.isThursdayOpen
  );
  const [thursdayOpenTime, setThursdayOpenTime] = useState<string>(
    data.thursdayOpenTime
  );
  const [thursdayCloseTime, setThursdayCloseTime] = useState<string>(
    data.thursdayCloseTime
  );
  const [isFridayOpen, setIsFridayOpen] = useState<boolean>(data.isFridayOpen);
  const [fridayOpenTime, setFridayOpenTime] = useState<string>(
    data.fridayOpenTime
  );
  const [fridayCloseTime, setFridayCloseTime] = useState<string>(
    data.fridayCloseTime
  );
  const [isSaturdayOpen, setIsSaturdayOpen] = useState<boolean>(
    data.isSaturdayOpen
  );
  const [saturdayOpenTime, setSaturdayOpenTime] = useState<string>(
    data.saturdayOpenTime
  );
  const [saturdayCloseTime, setSaturdayCloseTime] = useState<string>(
    data.saturdayCloseTime
  );

  useEffect(() => {
    if (data) {
      setStartDate(data?.startDate)
      setEndDate(data.endDate);
      setIsSundayOpen(data.isSundayOpen);
      setSundayOpenTime(data.sundayOpenTime);
      setSundayCloseTime(data.sundayCloseTime);
      setIsMondayOpen(data.isMondayOpen);
      setMondayOpenTime(data.mondayOpenTime);
      setMondayCloseTime(data.mondayCloseTime);
      setIsTuesdayOpen(data.isTuesdayOpen);
      setTuesdayOpenTime(data.tuesdayOpenTime);
      setTuesdayCloseTime(data.tuesdayCloseTime);
      setIsWednesdayOpen(data.isWednesdayOpen);
      setWednesdayOpenTime(data.wednesdayOpenTime);
      setWednesdayCloseTime(data.wednesdayCloseTime);
      setIsThursdayOpen(data.isThursdayOpen);
      setThursdayOpenTime(data.thursdayOpenTime);
      setThursdayCloseTime(data.thursdayCloseTime);
      setIsFridayOpen(data.isFridayOpen);
      setFridayOpenTime(data.fridayOpenTime);
      setFridayCloseTime(data.fridayCloseTime);
      setIsSaturdayOpen(data.isSaturdayOpen);
      setSaturdayOpenTime(data.saturdayOpenTime);
      setSaturdayCloseTime(data.saturdayCloseTime);
      queueMicrotask(() => {
        isHydratingRef.current = false;
      });
    }
  }, [data])

  useEffect(() => {
    if (!isHydratingRef.current) {
      const addOpenHourData: OpenHourType = {
        id: data.id,
        locationId: data.locationId,
        startDate: startDate,
        endDate: endDate,
        isSundayOpen: isSundayOpen,
        sundayOpenTime: sundayOpenTime,
        sundayCloseTime: sundayCloseTime,
        isMondayOpen: isMondayOpen,
        mondayOpenTime: mondayOpenTime,
        mondayCloseTime: mondayCloseTime,
        isTuesdayOpen: isTuesdayOpen,
        tuesdayOpenTime: tuesdayOpenTime,
        tuesdayCloseTime: tuesdayCloseTime,
        isWednesdayOpen: isWednesdayOpen,
        wednesdayOpenTime: wednesdayOpenTime,
        wednesdayCloseTime: wednesdayCloseTime,
        isThursdayOpen: isThursdayOpen,
        thursdayOpenTime: thursdayOpenTime,
        thursdayCloseTime: thursdayCloseTime,
        isFridayOpen: isFridayOpen,
        fridayOpenTime: fridayOpenTime,
        fridayCloseTime: fridayCloseTime,
        isSaturdayOpen: isSaturdayOpen,
        saturdayOpenTime: saturdayOpenTime,
        saturdayCloseTime: saturdayCloseTime,
        uniqueId: uniqueNumber
      };

      changeDataOnOpenHour(addOpenHourData, index);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate,
    endDate,
    isSundayOpen,
    sundayOpenTime,
    sundayCloseTime,
    isMondayOpen,
    mondayOpenTime,
    mondayCloseTime,
    isTuesdayOpen,
    tuesdayOpenTime,
    tuesdayCloseTime,
    isWednesdayOpen,
    wednesdayOpenTime,
    wednesdayCloseTime,
    isThursdayOpen,
    thursdayOpenTime,
    thursdayCloseTime,
    isFridayOpen,
    fridayOpenTime,
    fridayCloseTime,
    isSaturdayOpen,
    saturdayOpenTime,
    saturdayCloseTime,
  ]);

  return (
    <>
      <div className="flex flex-row justify-between mt-5">
        <div className="text-[12px] font-bold">
          Open Hour Block
        </div>

        <div
          className="text-blue-600 text-[12px] cursor-pointer hover:underline"
          onClick={() => deleteTheFleetOpenHour(uniqueNumber)}
        >
          Delete
        </div>
      </div>
      <hr />

      <div className="flex flex-row gap-4 px-2 mt-1.5">
        <div className="w-1/2 relative">
          <label className={`block text-xs font-medium text-[#667085]`}>Start Date</label>
          <CustomDateField
            value={startDate}
            onChange={setStartDate}
            required={false}
            className="w-full border border-gray-300 rounded-xl px-3 py-4 text-sm outline-none focus:border-primary"
          />
        </div>

        <div className="w-1/2 relative">
          <label className={`block text-xs font-medium text-[#667085]`}>End Date</label>
          <CustomDateField
            value={endDate}
            onChange={setEndDate}
            required={false}
            className="w-full border border-gray-300 rounded-xl px-3 py-4 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>

      <div className="flex flex-row mt-5 gap-2">
        <div className="w-[5%] text-gray-500 text-start text-[12px] font-bold">
          Open?
        </div>

        <div className="w-[11%] text-gray-500 text-start text-[12px] font-bold">
          Day of week
        </div>
        <div className="w-[28%] text-gray-500 text-start text-[12px] font-bold">
          Open Time
        </div>

        <div className="w-[28%] text-gray-500 text-start text-[12px] font-bold">
          Close Time
        </div>

        <div className="w-[28%] text-gray-500 text-start text-[12px] font-bold">
          Lunch Time Interval
        </div>
      </div>
      <hr className="mb-0" />

      <div className="mt-[1.25rem]">
        <div className="flex flex-row gap-2">
          <div className="w-[5%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full"
              type="checkbox"
              checked={isSundayOpen}
              disabled={disabled}
              onChange={() => setIsSundayOpen(!isSundayOpen)}
            />
          </div>

          <div className="w-[11%] text-gray-500 text-start text-[12px] my-1">
            Sunday
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={sundayOpenTime}
              disabled={disabled}
              onChange={(e) => setSundayOpenTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={sundayCloseTime}
              disabled={disabled}
              onChange={(e) => setSundayCloseTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="text"
              disabled={disabled}
            />
          </div>
        </div>

        <hr />

        <div className="flex flex-row gap-2">
          <div className="w-[5%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full"
              type="checkbox"
              checked={isMondayOpen}
              disabled={disabled}
              onChange={() => setIsMondayOpen(!isMondayOpen)}
            />
          </div>

          <div className="w-[11%] text-gray-500 text-start text-[12px] my-1">
            Monday
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={mondayOpenTime}
              disabled={disabled}
              onChange={(e) => setMondayOpenTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={mondayCloseTime}
              disabled={disabled}
              onChange={(e) => setMondayCloseTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="text"
              disabled={disabled}
            />
          </div>
        </div>

        <hr />

        <div className="flex flex-row gap-2">
          <div className="w-[5%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full"
              type="checkbox"
              checked={isTuesdayOpen}
              disabled={disabled}
              onChange={() => setIsTuesdayOpen(!isTuesdayOpen)}
            />
          </div>

          <div className="w-[11%] text-gray-500 text-start text-[12px] my-1">
            Tuesday
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={tuesdayOpenTime}
              disabled={disabled}
              onChange={(e) => setTuesdayOpenTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={tuesdayCloseTime}
              disabled={disabled}
              onChange={(e) => setTuesdayCloseTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="text"
              disabled={disabled}
            />
          </div>
        </div>

        <hr />

        <div className="flex flex-row gap-2">
          <div className="w-[5%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full"
              type="checkbox"
              checked={isWednesdayOpen}
              disabled={disabled}
              onChange={() => setIsWednesdayOpen(!isWednesdayOpen)}
            />
          </div>

          <div className="w-[11%] text-gray-500 text-start text-[12px] my-1">
            Wednesday
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={wednesdayOpenTime}
              disabled={disabled}
              onChange={(e) => setWednesdayOpenTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={wednesdayCloseTime}
              disabled={disabled}
              onChange={(e) => setWednesdayCloseTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="text"
              disabled={disabled}
            />
          </div>
        </div>

        <hr />

        <div className="flex flex-row gap-2">
          <div className="w-[5%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full"
              type="checkbox"
              checked={isThursdayOpen}
              disabled={disabled}
              onChange={() => setIsThursdayOpen(!isThursdayOpen)}
            />
          </div>

          <div className="w-[11%] text-gray-500 text-start text-[12px] my-1">
            Thursday
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={thursdayOpenTime}
              disabled={disabled}
              onChange={(e) => setThursdayOpenTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={thursdayCloseTime}
              disabled={disabled}
              onChange={(e) => setThursdayCloseTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="text"
              disabled={disabled}
            />
          </div>
        </div>

        <hr />

        <div className="flex flex-row gap-2">
          <div className="w-[5%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full"
              type="checkbox"
              checked={isFridayOpen}
              disabled={disabled}
              onChange={() => setIsFridayOpen(!isFridayOpen)}
            />
          </div>

          <div className="w-[11%] text-gray-500 text-start text-[12px] my-1">
            Friday
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={fridayOpenTime}
              disabled={disabled}
              onChange={(e) => setFridayOpenTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={fridayCloseTime}
              disabled={disabled}
              onChange={(e) => setFridayCloseTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="text"
              disabled={disabled}
            />
          </div>
        </div>

        <hr />

        <div className="flex flex-row gap-2">
          <div className="w-[5%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full"
              type="checkbox"
              checked={isSaturdayOpen}
              disabled={disabled}
              onChange={() => setIsSaturdayOpen(!isSaturdayOpen)}
            />
          </div>

          <div className="w-[11%] text-gray-500 text-start text-[12px] my-1">
            Saturday
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={saturdayOpenTime}
              disabled={disabled}
              onChange={(e) => setSaturdayOpenTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="time"
              value={saturdayCloseTime}
              disabled={disabled}
              onChange={(e) => setSaturdayCloseTime(e.target.value)}
            />
          </div>

          <div className="w-[28%] text-gray-500 text-start text-[12px] my-1">
            <input
              className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
              type="text"
              disabled={disabled}
            />
          </div>
        </div>
        <hr />
      </div>
    </>
  );
}
